import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

export interface CachedEntry<T = any> {
  /** Unique key for the cache entry */
  key: string;

  /** The cached data */
  data: T;

  /** When this data was cached */
  cachedAt: Date;

  /** Expiry time for this cache entry */
  expiresAt: Date;
}

export interface CacheContextValue {
  /** Get cached data by key */
  get: <T = any>(key: string) => T | null;

  /** Store data in cache with optional TTL */
  set: <T = any>(key: string, data: T, ttlMs?: number) => void;

  /** Remove cached data by key */
  remove: (key: string) => void;

  /** Clear all cached data */
  clear: () => void;

  /** Check if key exists and is not expired */
  has: (key: string) => boolean;

  /** Get all cache keys */
  keys: () => string[];

  /** Get cache size */
  size: () => number;
}

const CacheContext = createContext<CacheContextValue | undefined>(undefined);

export interface CacheProviderProps {
  children: React.ReactNode;

  /** Options for cache behavior */
  options?: {
    /** Default TTL for cache entries in milliseconds (default: 8 hours) */
    defaultTtlMs?: number;

    /** Maximum number of cache entries (default: 100) */
    maxEntries?: number;

    /** Storage key prefix for localStorage (default: "app-cache") */
    storagePrefix?: string;

    /** Cleanup interval for expired entries in milliseconds (default: 10 minutes) */
    cleanupIntervalMs?: number;
  };
}

const DEFAULT_TTL = 8 * 60 * 60 * 1000; // 8 hours
const DEFAULT_STORAGE_PREFIX = "app-cache";

export const CacheProvider: React.FC<CacheProviderProps> = ({
  children,
  options = {},
}) => {
  const {
    defaultTtlMs = DEFAULT_TTL,
    maxEntries = 100,
    storagePrefix = DEFAULT_STORAGE_PREFIX,
    cleanupIntervalMs = 10 * 60 * 1000, // 10 minutes
  } = options;

  // Use ref for cache entries to avoid dependency issues
  const cacheEntriesRef = useRef<Map<string, CachedEntry>>(new Map());
  //const [, forceUpdate] = useState({});

  // Force re-render when cache changes (for debugging/monitoring)
  // const triggerUpdate = useCallback(() => {
  //   forceUpdate({});
  // }, []);

  // Check if localStorage is available - make it stable with no dependencies
  const hasLocalStorage = () => {
    try {
      if (typeof window === "undefined") return false;
      const test = "__cache_test__";
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  };

  // Generate storage key - make it stable with no dependencies
  const getStorageKey = (key: string) => {
    return `${storagePrefix}:${key}`;
  };

  // Load single entry from localStorage - make it stable with no dependencies
  const loadFromStorage = (key: string): CachedEntry | null => {
    if (!hasLocalStorage()) return null;

    try {
      const storageKey = getStorageKey(key);
      const stored = localStorage.getItem(storageKey);
      if (!stored) return null;

      const parsed = JSON.parse(stored);
      return {
        ...parsed,
        cachedAt: new Date(parsed.cachedAt),
        expiresAt: new Date(parsed.expiresAt),
      };
    } catch (error) {
      console.debug(`Cache: Failed to load ${key} from storage:`, error);
      return null;
    }
  };

  // Save single entry to localStorage - make it stable with no dependencies
  const saveToStorage = (key: string, entry: CachedEntry) => {
    if (!hasLocalStorage()) return;

    try {
      const storageKey = getStorageKey(key);
      localStorage.setItem(storageKey, JSON.stringify(entry));
    } catch (error) {
      console.debug(`Cache: Failed to save ${key} to storage:`, error);
    }
  };

  // Remove single entry from localStorage - make it stable with no dependencies
  const removeFromStorage = (key: string) => {
    if (!hasLocalStorage()) return;

    try {
      const storageKey = getStorageKey(key);
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.debug(`Cache: Failed to remove ${key} from storage:`, error);
    }
  };

  // Create stable methods with no dependencies
  const contextValue: CacheContextValue = useMemo(() => {
    const get = <T = any,>(key: string): T | null => {
      // First check memory cache
      let entry: any = cacheEntriesRef.current.get(key);

      // If not in memory, try localStorage
      if (!entry) {
        entry = loadFromStorage(key);
        if (entry) {
          // Add to memory cache
          cacheEntriesRef.current.set(key, entry);
        }
      }

      if (!entry) return null;

      // Check if expired
      if (new Date() > entry.expiresAt) {
        cacheEntriesRef.current.delete(key);
        removeFromStorage(key);
        return null;
      }

      return entry.data as T;
    };

    const set = <T = any,>(
      key: string,
      data: T,
      ttlMs: number = defaultTtlMs
    ) => {
      const now = new Date();
      const expiresAt = new Date(now.getTime() + ttlMs);

      const entry: CachedEntry<T> = {
        key,
        data,
        cachedAt: now,
        expiresAt,
      };

      // Enforce max entries limit by removing oldest
      if (cacheEntriesRef.current.size >= maxEntries) {
        const oldestKey = Array.from(cacheEntriesRef.current.entries()).sort(
          ([, a], [, b]) => a.cachedAt.getTime() - b.cachedAt.getTime()
        )[0][0];
        cacheEntriesRef.current.delete(oldestKey);
        removeFromStorage(oldestKey);
        console.debug(
          `Cache: Removed oldest entry ${oldestKey} to stay within limit`
        );
      }

      cacheEntriesRef.current.set(key, entry);

      // Save to localStorage
      saveToStorage(key, entry);
      console.debug(
        `Cache: Stored ${key}, expires at ${expiresAt.toISOString()}`
      );
    };

    const remove = (key: string) => {
      cacheEntriesRef.current.delete(key);
      removeFromStorage(key);
      console.debug(`Cache: Removed ${key}`);
    };

    const clear = () => {
      // Clear memory cache
      cacheEntriesRef.current.clear();

      // Clear localStorage entries
      if (hasLocalStorage()) {
        try {
          const prefix = `${storagePrefix}:`;
          const keysToRemove: string[] = [];

          for (let i = 0; i < localStorage.length; i++) {
            const storageKey = localStorage.key(i);
            if (storageKey && storageKey.startsWith(prefix)) {
              keysToRemove.push(storageKey);
            }
          }

          keysToRemove.forEach((key) => localStorage.removeItem(key));
          console.debug(
            `Cache: Cleared ${keysToRemove.length} entries from localStorage`
          );
        } catch (error) {
          console.warn("Cache: Failed to clear localStorage:", error);
        }
      }

      console.debug("Cache: Cleared all entries");
    };

    const has = (key: string): boolean => {
      return get(key) !== null;
    };

    const keys = (): string[] => {
      return Array.from(cacheEntriesRef.current.keys());
    };

    const size = (): number => {
      return cacheEntriesRef.current.size;
    };

    return { get, set, remove, clear, has, keys, size };
  }, []); // No dependencies - completely stable!

  // Load all cache entries from localStorage on mount
  useEffect(() => {
    if (!hasLocalStorage()) return;

    try {
      const prefix = `${storagePrefix}:`;
      const restoredEntries = new Map<string, CachedEntry>();

      // Iterate through localStorage to find our cache entries
      for (let i = 0; i < localStorage.length; i++) {
        const storageKey = localStorage.key(i);
        if (storageKey && storageKey.startsWith(prefix)) {
          const cacheKey = storageKey.substring(prefix.length);
          const entry = loadFromStorage(cacheKey);

          if (entry) {
            // Check if expired
            if (new Date() > entry.expiresAt) {
              removeFromStorage(cacheKey);
            } else {
              restoredEntries.set(cacheKey, entry);
            }
          }
        }
      }

      cacheEntriesRef.current = restoredEntries;
      console.debug(
        `Cache: Restored ${restoredEntries.size} entries from localStorage`
      );
    } catch (error) {
      console.warn("Cache: Failed to restore from localStorage:", error);
    }
  }, [storagePrefix]);

  // Cleanup expired entries periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = new Date();
      let cleanedCount = 0;

      for (const [key, entry] of cacheEntriesRef.current.entries()) {
        if (now > entry.expiresAt) {
          cacheEntriesRef.current.delete(key);
          removeFromStorage(key);
          cleanedCount++;
        }
      }

      if (cleanedCount > 0) {
        console.debug(`Cache: Cleaned up ${cleanedCount} expired entries`);
      }
    }, cleanupIntervalMs);

    return () => clearInterval(cleanupInterval);
  }, [cleanupIntervalMs]);

  return (
    <CacheContext.Provider value={contextValue}>
      {children}
    </CacheContext.Provider>
  );
};

// Custom hook for using the cache context
export const useCache = (): CacheContextValue => {
  const context = useContext(CacheContext);
  if (context === undefined) {
    throw new Error("useCache must be used within a CacheProvider");
  }
  return context;
};
